// BUILD-046 section C (Owner direction of 2026-10-21, Update task and follow-up accountability): the Update task form - its draft, the rules the server enforces
// (mirrored here so the form says what is wrong before anything is sent; the server stays authoritative and its words are shown when it refuses), and the request body.
//
//   The body carries ONLY what changed. A routine note therefore never moves a date or clears a missed follow-up: those change only when the person changes the field.
//   A due date or follow-up date that already had a value needs a short reason to change or clear (the first entry needs none).
//   Waiting and Blocked need the explanation (the note) when the task MOVES into them, a next action, and a follow-up date that has not passed. A task that STAYS Waiting or Blocked is checked only
//   for what this update changes (Owner direction 2026-10-22): it may not remove the next action or the follow-up date, a follow-up date set now is today or later, and a note, a due date or
//   an untouched legacy record is saved as it is (a missed follow-up stays missed; nothing is filled in).
//   A follow-up date entered now is today or later; the activity date of a note may be earlier (backdating) but never later. All days are the Phoenix calendar day.
//
// Pure module: no React, no I/O. Type-only import of the request shape. Erasable TypeScript only.
// CHANGED 2026-10-23 (BUILD-046 G4, Builder decision requester-edit-authority-final-local-check, October 23 addendum, item 1): the Task due date is the REQUESTER's. The person doing a task someone else issued changes the next action and the follow-up
// date here, and sees the due date as a fixed value with the reason; the form then neither offers it nor sends it (the server refuses a request that names it, however it arrives). `deadline` (default true: the requester, or a task issued to oneself) says
// whether this Update may change the due date; the page passes it from the same rule that offers Edit task (taskActionOffers.ts).
import { isFutureDate, isIsoDate } from "./dateLabels";
import { personDisplay } from "./displayNames";
import type { WorkProgressInput } from "../../api/workShared";

export type UpdateStatus = "active" | "in_progress" | "waiting" | "blocked";

export const UPDATE_STATUSES: readonly { value: UpdateStatus; label: string }[] = Object.freeze([
  { value: "active", label: "Active" },
  { value: "in_progress", label: "In progress" },
  { value: "waiting", label: "Waiting" },
  { value: "blocked", label: "Blocked" },
]);

/** The task as the form reads it. */
export interface UpdateTaskView {
  id?: unknown;
  title?: unknown;
  status?: unknown;
  nextAction?: unknown;
  followUpDate?: unknown;
  dueDate?: unknown;
}

/** What the form holds while it is open. Dates are YYYY-MM-DD or empty. */
export interface UpdateDraft {
  status: UpdateStatus;
  /** What happened / progress note. */
  note: string;
  /** The day the work happened. Defaults to today (Phoenix); an earlier day is allowed, a later one is not. */
  activityDate: string;
  nextAction: string;
  followUpDate: string;
  dueDate: string;
  /** Why an existing date is changing; asked only when one is. */
  reason: string;
}

export type UpdateField = "status" | "note" | "activityDate" | "nextAction" | "followUpDate" | "dueDate" | "reason" | "form";

export interface UpdateIssue {
  field: UpdateField;
  message: string;
}

const clean = (value: unknown): string => (typeof value === "string" ? value.trim() : "");
const dateOf = (value: unknown): string => (typeof value === "string" && isIsoDate(value) ? value : "");

export function isUpdateStatus(value: unknown): value is UpdateStatus {
  return value === "active" || value === "in_progress" || value === "waiting" || value === "blocked";
}

/** The draft a form opens with: the task as it is now, a blank note, and today as the activity date. */
export function initialUpdateDraft(task: UpdateTaskView, today: string): UpdateDraft {
  return {
    status: isUpdateStatus(task.status) ? task.status : "active",
    note: "",
    activityDate: today,
    nextAction: clean(task.nextAction),
    followUpDate: dateOf(task.followUpDate),
    dueDate: dateOf(task.dueDate),
    reason: "",
  };
}

export type FollowUpState = "none" | "upcoming" | "today" | "missed";

/** Where a follow-up date stands against the Phoenix day: missed (before today), today, upcoming, or none. */
export function followUpState(followUpDate: unknown, today: string): FollowUpState {
  const date = dateOf(followUpDate);
  if (date === "") return "none";
  return date < today ? "missed" : date === today ? "today" : "upcoming";
}

export interface DateChange {
  field: "followUpDate" | "dueDate";
  label: string;
  from: string;
  /** Empty when the date is being cleared. */
  to: string;
}

const DATE_LABELS: Readonly<Record<"followUpDate" | "dueDate", string>> = Object.freeze({ followUpDate: "Next follow-up date", dueDate: "Task due date" });

/** What the Update form says in place of the due date control when the person doing the task did not issue it. */
export const UPDATE_DEADLINE_LOCKED_LINE = "Set by the person who issued this task. You can change the next action and the follow-up date.";

/** The dates the draft changes (a first entry, a move or a clear). The Task due date is left out when this Update may not change it (`deadline` false). */
export function dateChanges(task: UpdateTaskView, draft: UpdateDraft, deadline: boolean = true): DateChange[] {
  const out: DateChange[] = [];
  for (const field of ["followUpDate", "dueDate"] as const) {
    if (field === "dueDate" && !deadline) continue;
    const from = dateOf(task[field]);
    const to = draft[field].trim();
    if (from !== to) out.push({ field, label: DATE_LABELS[field], from, to });
  }
  return out;
}

/** The changes that replace or clear a date that already had a value: these need the reason. */
export function replacedDates(task: UpdateTaskView, draft: UpdateDraft, deadline: boolean = true): DateChange[] {
  return dateChanges(task, draft, deadline).filter((change) => change.from !== "");
}

export function reasonRequired(task: UpdateTaskView, draft: UpdateDraft, deadline: boolean = true): boolean {
  return replacedDates(task, draft, deadline).length > 0;
}

function statusChanged(task: UpdateTaskView, draft: UpdateDraft): boolean {
  return draft.status !== task.status;
}

/** The request body: only what changed. Null when there is nothing to save. */
export function updateBody(task: UpdateTaskView, draft: UpdateDraft, today: string, deadline: boolean = true): WorkProgressInput | null {
  const body: WorkProgressInput = {};
  if (statusChanged(task, draft)) body.status = draft.status;
  const note = draft.note.trim();
  if (note !== "") body.note = draft.activityDate === "" || draft.activityDate === today ? { text: note } : { text: note, activityDate: draft.activityDate };
  const nextAction = draft.nextAction.trim();
  if (nextAction !== clean(task.nextAction)) body.nextAction = nextAction === "" ? null : nextAction;
  const changes = dateChanges(task, draft, deadline);
  for (const change of changes) body[change.field] = change.to === "" ? null : change.to;
  const reason = draft.reason.trim();
  if (changes.length > 0 && reason !== "") body.changeReason = reason;
  return Object.keys(body).length === 0 ? null : body;
}

/**
 * The latest progress note as one quiet line for a row ("Latest update, Oct 20" and the text). Null when the task has none or the row is shown in its restricted form.
 * CHANGED 2026-10-23 (BUILD-046 G4, October 23 addendum, item 3): when the task records WHO wrote that note (`lastProgressByName`, written with the note) the label names them ("Latest update by Eddie F., Oct 20"), so after a reassignment the
 * new assignee is not credited with the previous person's note. A note recorded before the author was kept has none: the label then names nobody (unknown stays unknown, and it is never the current assignee).
 */
export function latestUpdateLine(task: { lastProgressText?: unknown; lastActivityDate?: unknown; lastProgressByName?: unknown; redacted?: unknown }, formatDate: (date: string) => string): { label: string; text: string } | null {
  if (task.redacted === true) return null;
  const text = clean(task.lastProgressText);
  if (text === "") return null;
  const date = dateOf(task.lastActivityDate);
  const recorded = clean(task.lastProgressByName);
  const person = recorded === "" ? "" : personDisplay(recorded).short || recorded;
  const head = person === "" ? "Latest update" : "Latest update by " + person;
  return { label: date === "" ? head : head + ", " + formatDate(date), text };
}

const CHANGED_WORDS: Readonly<Record<string, string>> = Object.freeze({ status: "status", nextAction: "next action", followUpDate: "follow-up date", dueDate: "due date" });

/** The line shown after the server accepted an Update: what was saved, in the order note, then the fields that changed. */
export function updateSavedMessage(response: { noteSaved?: unknown; changed?: unknown }): string {
  const parts: string[] = [];
  if (response.noteSaved === true) parts.push("note");
  if (Array.isArray(response.changed)) for (const key of response.changed) if (typeof key === "string" && CHANGED_WORDS[key] !== undefined) parts.push(CHANGED_WORDS[key]);
  return parts.length === 0 ? "Update saved." : "Update saved (" + parts.join(", ") + ").";
}

export const MISSED_FOLLOW_UP_MESSAGE = "The follow-up date has passed. Choose a new follow-up date.";

/** Said on the Create and Edit forms (Owner direction 2026-10-22): Create and Edit never set Waiting or Blocked, and the server refuses them there; Update does, with its explanation. */
export const WAITING_SET_WITH_UPDATE_LINE = "Waiting and Blocked are set with Update. It asks what happened, the next action and a follow-up date.";
export const WAITING_KEEPS_ACCOUNTABILITY_LINE = "A Waiting or Blocked task keeps its next action and follow-up date. To change its status, use Update.";

/** Owner direction 2026-10-23: Edit and Update are different forms with different purposes, and each says so in one line. Edit changes the task; Update records progress. */
export const EDIT_TASK_HELP_LINE = "Changes the task itself: its title, description, priority and due date. To record progress, use Update.";
// CHANGED 2026-10-22 (BUILD-046 G1, Builder decision requester-edit-authority): Edit task is the requester's alone, so the line no longer sends the person doing the task to a form they cannot open.
export const UPDATE_TASK_HELP_LINE = "Records what happened and what comes next. The title, description and priority are changed by the person who issued the task, with Edit task.";
/** Owner decision D8: said on the Edit form when the person editing issued the task and someone else is doing it. */
export const EDIT_BY_REQUESTER_LINE = "You issued this task, so you can change what it is. The person doing it keeps its status and progress.";

/** What is wrong with the draft, in the server words where the server has them. An empty list means the form may be sent. */
export function validateUpdate(task: UpdateTaskView, draft: UpdateDraft, today: string, deadline: boolean = true): UpdateIssue[] {
  const issues: UpdateIssue[] = [];
  const note = draft.note.trim();
  if (note !== "") {
    if (!isIsoDate(draft.activityDate)) issues.push({ field: "activityDate", message: "Choose the day this happened." });
    else if (isFutureDate(draft.activityDate, today)) issues.push({ field: "activityDate", message: "A note records work already done. Record work not yet done as a follow-up, due date or appointment." });
  }
  for (const field of ["followUpDate", "dueDate"] as const) {
    if (field === "dueDate" && !deadline) continue;
    const value = draft[field].trim();
    if (value !== "" && !isIsoDate(value)) issues.push({ field, message: "Choose a real date." });
  }
  const follow = draft.followUpDate.trim();
  const followChanged = follow !== dateOf(task.followUpDate);
  if (followChanged && isIsoDate(follow) && follow < today) issues.push({ field: "followUpDate", message: "Choose a follow-up date of today or later." });
  if (draft.status === "waiting" || draft.status === "blocked") {
    const word = draft.status === "waiting" ? "Waiting" : "Blocked";
    const entering = statusChanged(task, draft);
    const changesNextAction = draft.nextAction.trim() !== clean(task.nextAction);
    if (entering && note === "") {
      issues.push({ field: "note", message: draft.status === "waiting" ? "Say what this task is waiting on." : "Say what is blocking this task." });
    }
    if ((entering || changesNextAction) && draft.nextAction.trim() === "") issues.push({ field: "nextAction", message: word + " needs a next action." });
    if (entering || followChanged) {
      if (follow === "") issues.push({ field: "followUpDate", message: word + " needs a follow-up date." });
      // A date set now is held to today or later above; a date left as it was and already past cannot start a Waiting or Blocked task.
      else if (!followChanged && isIsoDate(follow) && follow < today) issues.push({ field: "followUpDate", message: MISSED_FOLLOW_UP_MESSAGE });
    }
  }
  if (reasonRequired(task, draft, deadline) && draft.reason.trim() === "") issues.push({ field: "reason", message: "Add a short reason for changing the date." });
  if (issues.length === 0 && updateBody(task, draft, today, deadline) === null) issues.push({ field: "form", message: "Add what happened, or change a field." });
  return issues;
}

/** The words that go with a field name the server reports (details.field). */
export function fieldOfServerError(field: unknown): UpdateField {
  switch (field) {
    case "note":
    case "note.text":
      return "note";
    case "note.activityDate":
      return "activityDate";
    case "changeReason":
      return "reason";
    case "status":
    case "nextAction":
    case "followUpDate":
    case "dueDate":
      return field;
    default:
      return "form";
  }
}

/** A refusal or failure as form messages: the server message beside the field it names (or the form). The draft is untouched either way. */
export function issuesFromError(error: unknown, fallback = "The update was not saved."): UpdateIssue[] {
  const record = typeof error === "object" && error !== null ? (error as { message?: unknown; details?: unknown }) : {};
  const message = typeof record.message === "string" && record.message.trim() !== "" ? record.message.trim() : fallback;
  const details = typeof record.details === "object" && record.details !== null ? (record.details as { field?: unknown }) : {};
  return [{ field: fieldOfServerError(details.field), message }];
}

/** The label of the note field: it asks for the explanation (and says it is required) only when the task MOVES into Waiting or Blocked. */
export function noteLabel(task: UpdateTaskView, draft: UpdateDraft): string {
  if (draft.status === "waiting" && statusChanged(task, draft)) return "Why is it waiting? (required)";
  if (draft.status === "blocked" && statusChanged(task, draft)) return "What is blocking it? (required)";
  return "What happened / progress note";
}
